/**
 * G2eGente - Funcionário Responde Pesquisa de Clima
 * Interface móvel para resposta anônima
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { climateSurveyAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft,
  ThermometerSun,
  Loader2,
  CheckCircle2,
  Shield,
  ChevronRight,
  ChevronLeft,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const SCALE_1_5_OPTIONS = [
  { value: '1', label: 'Discordo totalmente' },
  { value: '2', label: 'Discordo' },
  { value: '3', label: 'Neutro' },
  { value: '4', label: 'Concordo' },
  { value: '5', label: 'Concordo totalmente' }
];

const SCALE_1_10_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1)
}));

export default function EmployeeClimateSurvey() {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Check if already answered (localStorage)
    const answeredSurveys = JSON.parse(localStorage.getItem('answeredClimateSurveys') || '[]');
    if (answeredSurveys.includes(surveyId)) {
      setSubmitted(true);
      setLoading(false);
      return;
    }
    
    fetchSurvey();
  }, [surveyId]);

  const fetchSurvey = async () => {
    try {
      const { data } = await climateSurveyAPI.employeeGetSurvey(surveyId);
      setSurvey(data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Você não está elegível para esta pesquisa');
      } else {
        toast.error('Erro ao carregar pesquisa');
      }
      navigate('/m/employee');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (perguntaId, valor) => {
    setAnswers(prev => ({
      ...prev,
      [perguntaId]: valor
    }));
  };

  const handleNext = () => {
    const currentPergunta = survey.perguntas[currentQuestion];
    if (currentPergunta.obrigatoria && !answers[currentPergunta.id]) {
      toast.error('Esta pergunta é obrigatória');
      return;
    }
    setCurrentQuestion(prev => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentQuestion(prev => prev - 1);
  };

  const handleSubmit = async () => {
    // Validate required questions
    for (const pergunta of survey.perguntas) {
      if (pergunta.obrigatoria && !answers[pergunta.id]) {
        toast.error(`Por favor, responda a pergunta: "${pergunta.texto.substring(0, 50)}..."`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const respostas = Object.entries(answers).map(([pergunta_id, valor]) => ({
        pergunta_id,
        valor
      }));

      await climateSurveyAPI.employeeSubmit(surveyId, {
        respostas,
        departamento: survey.employee_departamento
      });

      // Mark as answered in localStorage
      const answeredSurveys = JSON.parse(localStorage.getItem('answeredClimateSurveys') || '[]');
      answeredSurveys.push(surveyId);
      localStorage.setItem('answeredClimateSurveys', JSON.stringify(answeredSurveys));

      setSubmitted(true);
      toast.success('Resposta enviada com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Already submitted
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Obrigado!</h2>
            <p className="text-slate-600 mb-6">
              Sua resposta foi enviada com sucesso. Sua participação é anônima e confidencial.
            </p>
            <Button onClick={() => navigate('/m/employee')} className="bg-cyan-600 hover:bg-cyan-700">
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!survey) return null;

  const totalQuestions = survey.perguntas.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const currentPergunta = survey.perguntas[currentQuestion];
  const isLastQuestion = currentQuestion === totalQuestions - 1;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/m/employee')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-semibold text-slate-800 line-clamp-1">{survey.titulo}</h1>
              <p className="text-xs text-slate-500">Pergunta {currentQuestion + 1} de {totalQuestions}</p>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Confidentiality Notice (first question only) */}
      {currentQuestion === 0 && (
        <div className="mx-4 mt-4 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
          <div className="flex items-center gap-2 text-cyan-700">
            <Shield className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">
              <strong>Suas respostas são 100% anônimas</strong> e não podem ser identificadas individualmente.
            </p>
          </div>
        </div>
      )}

      {/* Question Card */}
      <div className="p-4">
        <Card className="mb-4">
          <CardContent className="pt-6">
            <p className="text-lg font-medium text-slate-800 mb-6">
              {currentPergunta.texto}
              {currentPergunta.obrigatoria && <span className="text-red-500 ml-1">*</span>}
            </p>

            {/* Scale 1-5 */}
            {currentPergunta.tipo === 'escala_1_5' && (
              <div className="space-y-3">
                {SCALE_1_5_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      answers[currentPergunta.id] === opt.value
                        ? 'border-cyan-500 bg-cyan-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleAnswer(currentPergunta.id, opt.value)}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      answers[currentPergunta.id] === opt.value
                        ? 'border-cyan-500 bg-cyan-500'
                        : 'border-slate-300'
                    }`}>
                      {answers[currentPergunta.id] === opt.value && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    <span className={`flex-1 ${
                      answers[currentPergunta.id] === opt.value ? 'text-cyan-700 font-medium' : 'text-slate-700'
                    }`}>
                      {opt.label}
                    </span>
                    <span className={`text-lg font-bold ${
                      answers[currentPergunta.id] === opt.value ? 'text-cyan-600' : 'text-slate-400'
                    }`}>
                      {opt.value}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* Scale 1-10 (NPS style) */}
            {currentPergunta.tipo === 'escala_1_10' && (
              <div className="flex flex-wrap gap-2 justify-center">
                {SCALE_1_10_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(currentPergunta.id, opt.value)}
                    className={`w-12 h-12 rounded-lg border-2 font-bold text-lg transition-all ${
                      answers[currentPergunta.id] === opt.value
                        ? 'border-cyan-500 bg-cyan-500 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-cyan-300'
                    }`}
                  >
                    {opt.value}
                  </button>
                ))}
              </div>
            )}

            {/* Yes/No */}
            {currentPergunta.tipo === 'sim_nao' && (
              <div className="flex gap-4">
                {[
                  { value: 'true', label: 'Sim' },
                  { value: 'false', label: 'Não' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(currentPergunta.id, opt.value)}
                    className={`flex-1 p-4 rounded-lg border-2 font-medium text-lg transition-all ${
                      answers[currentPergunta.id] === opt.value
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-slate-200 text-slate-600 hover:border-cyan-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Text */}
            {currentPergunta.tipo === 'texto' && (
              <Textarea
                value={answers[currentPergunta.id] || ''}
                onChange={(e) => handleAnswer(currentPergunta.id, e.target.value)}
                placeholder="Digite sua resposta..."
                rows={4}
                className="text-base"
              />
            )}

            {/* Multiple Choice */}
            {currentPergunta.tipo === 'multipla_escolha' && currentPergunta.opcoes && (
              <div className="space-y-3">
                {currentPergunta.opcoes.map((opcao, i) => (
                  <label
                    key={i}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      answers[currentPergunta.id] === opcao
                        ? 'border-cyan-500 bg-cyan-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleAnswer(currentPergunta.id, opcao)}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      answers[currentPergunta.id] === opcao
                        ? 'border-cyan-500 bg-cyan-500'
                        : 'border-slate-300'
                    }`}>
                      {answers[currentPergunta.id] === opcao && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    <span className={answers[currentPergunta.id] === opcao ? 'text-cyan-700 font-medium' : 'text-slate-700'}>
                      {opcao}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-3">
          {currentQuestion > 0 && (
            <Button variant="outline" onClick={handlePrevious} className="flex-1">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
          )}
          
          {!isLastQuestion ? (
            <Button onClick={handleNext} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              Próxima
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar Respostas
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
